/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useContext } from "react";
import * as FaIcons from "react-icons/fa";
import * as AiIcons from "react-icons/ai";
import XMLParser from "react-xml-parser";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import axios from "axios";

import { SidebarData } from "./SidebarData";
import SubMenu from "./SubMenu";
import Search from "../search/Search";
import { SignOutIcon } from "./SignOutIcon";
import ExamMenu from "./subMenus/exam/ExamMenu";
import AdminMenu from "./subMenus/admin/AdminMenu";
import PhotoBox from "../global/PhotoBox";
import appContext from "../../context/app/appContext";
import studentContext from "../../context/student/studentContext";

const breakPoint = 1110;

const Sidebar = ({ pinned, setPinned, pinnable, setPinnable, setMargin }) => {
  const { privileges } = useContext(appContext);
  const { selectedStudent } = useContext(studentContext);

  const wrapperRef = useRef();
  const searchRef = useRef();

  const history = useHistory();

  const [sidebar, setSidebar] = useState(window.innerWidth >= breakPoint);
  const [searchClicked, setSearchClicked] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [logoSrc, setLogoSrc] = useState("");

  // fetch logo on mount
  useEffect(() => {
    async function getLogos() {
      try {
        const url =
          "https://geodesicpublicresources.blob.core.windows.net/school-logos?restype=container&comp=list&include=metadata";
        const res = await axios.get(url);
        const xml = new XMLParser().parseFromString(res.data);
        const possible = xml.getElementsByTagName("Metadata").map((m) => ({
          schoolUrl: m.children[0].value,
          thisUrl: m.children[1].value,
        }));
        const found = possible.find((o) =>
          window.location.href.includes(o.schoolUrl)
        );
        setLogoSrc(found ? found.thisUrl : possible[0]?.thisUrl);
      } catch {
        setLogoSrc(
          "https://geodesicpublicresources.blob.core.windows.net/school-logos/Geodesic.png"
        );
      }
    }
    getLogos();
  }, []);

  // close sidebar on outside click if not pinned
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        !pinned
      ) {
        setSidebar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [pinned]);

  // close search on outside click
  useEffect(() => {
    const handleClickOutsideSearch = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchClicked(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideSearch);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideSearch);
  }, []);

  // handle resize → toggle pinnable
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      if (window.innerWidth > breakPoint) {
        setPinnable(true);
      } else {
        setPinnable(false);
        setSidebar(false);
        setPinned(false);
        setMargin("0");
      }
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [setPinned, setPinnable, setMargin]);

  const toggleSidebar = () => setSidebar((s) => !s);
  const togglePin = () => {
    if (pinned) {
      setPinned(false);
      setSidebar(false);
    } else {
      setPinned(true);
      setSidebar(true);
    }
  };

  return (
    <div ref={wrapperRef}>
      {/* Top navbar */}
      <div className="flex items-center h-[70px] px-2 bg-white">
        {/* menu toggle */}
        {!pinned && (
          <button
            onClick={toggleSidebar}
            className="text-2xl text-gray-800 p-4 hover:bg-gray-100 rounded-md transition"
          >
            <FaIcons.FaBars />
          </button>
        )}

        {/* search */}
        <div
          ref={searchRef}
          onClick={() => setSearchClicked(true)}
          className={`
            flex items-center transition-all duration-200
            ${searchClicked ? "w-full" : "w-3/5"}
            ${pinned ? "ml-[280px]" : ""}
          `}
        >
          <Search />
        </div>

        {/* when sidebar is closed & not searching: brand + logout in dark full-height bar */}
        {!sidebar && !searchClicked && (
          <div className="flex-1 flex justify-end">
            <div className="flex h-[70px] items-center bg-gray-800 text-white">
              <div
                onClick={() => {
                  history.push("/");
                }}
                className="flex items-center h-full px-6 no-underline transform transition-transform hover:scale-105 hover:cursor-pointer"
              >
                <img
                  src="/includa2.png"
                  alt="Logo"
                  className="bg-transparent h-20"
                />
              </div>
              <button
                onClick={() => {
                  /* sign out logic */
                }}
                className="flex items-center h-full px-6 hover:bg-gray-700 transition-colors"
              >
                <SignOutIcon size={25} />
              </button>
            </div>
          </div>
        )}

        {/* when sidebar is open or search active: just show logout as normal */}
        {(sidebar || searchClicked) && (
          <button
            onClick={() => {
              /* sign out logic */
            }}
            className="text-2xl text-gray-800 p-4 hover:bg-gray-100 rounded-md transition ml-auto"
          >
            <SignOutIcon />
          </button>
        )}
      </div>

      {/* Sidebar panel */}
      <nav
        className={`
          fixed top-0 left-0 h-screen w-[280px]
          nav-style flex flex-col transform transition-transform duration-300 z-10
          ${sidebar ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* sidebar header */}
        <div className="flex items-center justify-between p-2">
          <div
            onClick={() => {
              history.push("/");
            }}
            className="flex items-center space-x-2 text-yellow-400 text-3xl  hover:scale-105 hover:cursor-pointer transition-transform transform no-underline"
          >
            <img
              src="/includa2.png"
              alt="Logo"
              className="bg-transparent h-20"
            />
            <span className="fuzzy-bubbles-regular text-4x3">Includa</span>
          </div>
          {pinnable && (
            <button
              onClick={togglePin}
              className="text-white text-2xl p-1 hover:text-gray-300 transition"
            >
              {pinned ? <AiIcons.AiOutlineClose /> : <AiIcons.AiFillPushpin />}
            </button>
          )}
        </div>

        {/* menu items */}
        <div className="flex-1 overflow-auto">
          {SidebarData.map((item, i) => (
            <SubMenu
              key={i}
              pinned={pinned}
              setPinned={setPinned}
              showSidebar={toggleSidebar}
              item={item}
            />
          ))}
          <ExamMenu
            pinned={pinned}
            setPinned={setPinned}
            showSidebar={toggleSidebar}
          />
          {privileges === 1 && (
            <AdminMenu
              pinned={pinned}
              setPinned={setPinned}
              showSidebar={toggleSidebar}
            />
          )}
          {selectedStudent?.StudentId && (
            <div className="p-4 flex items-center space-x-4">
              <PhotoBox
                container="student-photos"
                id={selectedStudent.StudentId}
                height={50}
              />
              <div className="text-white">
                <div>
                  {selectedStudent.Surname}, {selectedStudent.FirstName} (
                  {selectedStudent.MisId})
                </div>
                <div>Year {selectedStudent.YearLevel}</div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
